import API from 'services/api';
import {
  REGEXES,
  FIELD_NAMES,
  STATUSES,
  SPLIT_FROM_REQUIRED_FIELDS,
  HOSTED_PAGE_TYPES
} from 'consts';
import {
  BENEFICIARY_TOKENIZATION_THANK_YOU,
  BENEFICIARY_TOKENIZATION_ERROR,
  APP_ROUTE_NOT_FOUND
} from 'routes/paths';
import { sendGetPageFailMessage } from 'services/iframeService';
import { pickBy } from 'lodash-es';
import history from 'shared/services/history';
import { displaySpinner, hideSpinner } from 'redux/actions/global';
import showErrorAlert, { parseError } from 'services/errorHandler';
import * as CONSTS from 'redux/constants';
import { redirectToError } from 'utilities/error';
import store from 'redux/store';
import { BENEFICIARY_FORM_FIELDS } from 'consts/beneficiary';
import { getRouteWithToken, trackSaveBneficiary } from 'utilities';

export const getBeneficiaryTokenizationPageStart = () => ({
  type: CONSTS.GET_BENEFICIARY_TOKENIZATION_PAGE_START
});

export const getBeneficiaryTokenizationPageSuccess = data => ({
  type: CONSTS.GET_BENEFICIARY_TOKENIZATION_PAGE_SUCCESS,
  payload: { data }
});

export const getBeneficiaryTokenizationPageFail = (error, data) => ({
  type: CONSTS.GET_BENEFICIARY_TOKENIZATION_PAGE_FAIL,
  payload: { error, data }
});

export const getPayoutMethodTypes = () => ({
  type: CONSTS.GET_PAYOUT_METHOD_TYPES_START
});

export const resetPayoutMethodTypes = () => ({
  type: CONSTS.RESET_PAYOUT_METHOD_TYPES
});

export const getPayoutMethodTypesSuccess = data => ({
  type: CONSTS.GET_PAYOUT_METHOD_TYPES_SUCCESS,
  payload: { data }
});

export const getPayoutMethodTypesFail = error => ({
  type: CONSTS.GET_PAYOUT_METHOD_TYPES_FAIL,
  payload: { error }
});

export const getPayoutRequiredFieldsStart = () => ({
  type: CONSTS.GET_PAYOUT_REQUIRED_FIELDS_START
});

export const resetPayoutRequiredFields = () => ({
  type: CONSTS.RESET_PAYOUT_REQUIRED_FIELDS
});

export const updatePayoutRequiredFields = data => ({
  type: CONSTS.UPDATE_PAYOUT_REQUIRED_FIELDS,
  payload: { data }
});

export const getPayoutRequiredFieldsSuccess = data => ({
  type: CONSTS.GET_PAYOUT_REQUIRED_FIELDS_SUCCESS,
  payload: { data }
});

export const getPayoutRequiredFieldsFail = error => ({
  type: CONSTS.GET_PAYOUT_REQUIRED_FIELDS_FAIL,
  payload: { error }
});

export const setAllBeneficiaryDataFetched = allFetched => ({
  type: CONSTS.SET_ALL_BENEFICIARY_DATA_FETCHED,
  payload: allFetched
});

export const createBeneficiaryStart = () => ({
  type: CONSTS.CREATE_BENEFICIARY_START
});

export const createBeneficiarySuccess = data => ({
  type: CONSTS.CREATE_BENEFICIARY_SUCCESS,
  payload: { data }
});

export const createBeneficiaryFail = error => ({
  type: CONSTS.CREATE_BENEFICIARY_FAIL,
  payload: { error }
});

export const updateBeneficiaryTokenizationPageStatus = status => ({
  type: CONSTS.UPDATE_BENEFICIARY_TOKENIZATION_PAGE_STATUS,
  payload: { status }
});

export const getBeneficiaryTokenizationPage =
  ({ token }) =>
  async dispatch => {
    const handleGetPageError = (error, url = '') => {
      const { status, data } = parseError(error);
      dispatch(getBeneficiaryTokenizationPageFail(status, data));
      redirectToError({ error, url });
    };

    if (!token) {
      sendGetPageFailMessage(HOSTED_PAGE_TYPES.beneficiaryTokenization)();
      return redirectToError({ url: APP_ROUTE_NOT_FOUND });
    }

    try {
      dispatch(displaySpinner());
      dispatch(getBeneficiaryTokenizationPageStart());

      let beneficiaryTokenizationPageObject;
      try {
        beneficiaryTokenizationPageObject = await API.get(
          `hosted/disburse/beneficiary/${token}`
        );
      } catch (error) {
        return handleGetPageError(error, APP_ROUTE_NOT_FOUND);
      }

      const { data: beneficiaryTokenizationPage } =
        beneficiaryTokenizationPageObject.data;
      const {
        valid_countries,
        geo_country: geoCountry,
        beneficiary_optional_fields: beneficiaryOptionalFields = {}
      } = beneficiaryTokenizationPage;

      const isGeoCountrySupported = Boolean(
        geoCountry &&
          (valid_countries.all_supported ||
            valid_countries.countries?.includes(geoCountry))
      );

      dispatch(
        getBeneficiaryTokenizationPageSuccess({
          ...beneficiaryTokenizationPage,
          beneficiary_geo_country_supported: isGeoCountrySupported,
          beneficiary_optional_fields: {
            ...beneficiaryOptionalFields,
            [FIELD_NAMES.identification_type]:
              beneficiaryOptionalFields[FIELD_NAMES.identification_type] ===
              'drivers_license'
                ? 'driver\'s_license'
                : beneficiaryOptionalFields[FIELD_NAMES.identification_type]
          }
        })
      );

      if (beneficiaryTokenizationPage?.status === STATUSES.done) {
        return history.push(`${BENEFICIARY_TOKENIZATION_THANK_YOU}?token=${token}`);
      }
    } catch (error) {
      handleGetPageError(error, BENEFICIARY_TOKENIZATION_ERROR);
    } finally {
      dispatch(
        hideSpinner({ meta: { pageType: HOSTED_PAGE_TYPES.beneficiaryTokenization } })
      );
    }
  };

export const getPayoutMethodTypesListHelper = async (token, country) => {
  const getRequiredFields = (beneficiaryTokenizationPage, country) => ({
    beneficiary_country: beneficiaryTokenizationPage.beneficiary_country || country
  });

  const beneficiaryTokenizationPageObject =
    store.getState().beneficiaryTokenization.beneficiaryTokenizationPage.data;
  const apiData = getRequiredFields(beneficiaryTokenizationPageObject, country);
  const payoutMethodTypesList = await API.get(
    `/hosted/disburse/beneficiary/${token}/payout_methods_types`,
    apiData
  );
  const { data: repsonseData } = payoutMethodTypesList.data;
  return repsonseData;
};

export const getPayoutMethodTypesList = (token, country) => async dispatch => {
  try {
    dispatch(getPayoutMethodTypes());
    const data = await getPayoutMethodTypesListHelper(token, country);
    dispatch(getPayoutMethodTypesSuccess(data));
  } catch (error) {
    const { status } = parseError(error);
    dispatch(getPayoutMethodTypesFail(status));
    redirectToError({ url: BENEFICIARY_TOKENIZATION_ERROR });
  }
};

export const getPayoutRequiredFieldsHelper = async (token, country, payoutMethodType) => {
  const getRequiredFields = (beneficiaryTokenizationPage, country, payoutMethodType) => ({
    payout_method_type: payoutMethodType,
    beneficiary_country: beneficiaryTokenizationPage.beneficiary_country || country
  });

  const beneficiaryTokenizationPageObject =
    store.getState().beneficiaryTokenization.beneficiaryTokenizationPage.data;
  const apiData = getRequiredFields(
    beneficiaryTokenizationPageObject,
    country,
    payoutMethodType
  );
  const payoutMethodTypesList = await API.get(
    `hosted/disburse/beneficiary/${token}/payout_required_fields`,
    apiData
  );
  const {
    data: { beneficiary_required_fields: benficiaryRequiredFields = [] }
  } = payoutMethodTypesList.data;
  return [...benficiaryRequiredFields, ...addMandatoryFields(benficiaryRequiredFields)];
};

const cannotRecoverFromRequiredFieldsError = () => {
  const { beneficiaryTokenization: beneficiaryTokenizationState } = store.getState();
  const { valid_countries } =
    beneficiaryTokenizationState.beneficiaryTokenizationPage.data;
  const { data: payoutMethodTypes } = beneficiaryTokenizationState.payoutMethodTypesList;

  const onlyOneCountry =
    !valid_countries.all_supported && valid_countries.countries.length === 1;
  const onlyOnePayoutMethodType = payoutMethodTypes.length === 1;

  const cannotRecover = onlyOneCountry && onlyOnePayoutMethodType;
  return cannotRecover;
};

export const handleGetRequiredFieldsError = (error, dispatch) => {
  const cannotRecover = cannotRecoverFromRequiredFieldsError();
  if (cannotRecover) {
    redirectToError({ url: BENEFICIARY_TOKENIZATION_ERROR });
  } else {
    showErrorAlert({ error });
  }
  const { status } = parseError(error);
  dispatch(getPayoutRequiredFieldsFail(status));
};

export const getPayoutRequiredFields =
  (token, country, payoutMethodType) => async dispatch => {
    try {
      dispatch(getPayoutRequiredFieldsStart());
      const data = await getPayoutRequiredFieldsHelper(token, country, payoutMethodType);
      dispatch(getPayoutRequiredFieldsSuccess(data));
    } catch (error) {
      handleGetRequiredFieldsError(error, dispatch);
    }
  };

const addMandatoryFields = fields => {
  const extraFields = [FIELD_NAMES.identification_type, FIELD_NAMES.identification_value];
  const fieldsData = {
    identification_type: {
      name: FIELD_NAMES.identification_type,
      regex: REGEXES.identification_type
    },
    identification_value: {
      name: FIELD_NAMES.identification_value,
      regex: REGEXES.identification_value
    }
  };

  return extraFields
    .filter(fieldName => !fields.find(field => field.name === fieldName))
    .map(fieldName => {
      return fieldsData[fieldName];
    });
};

export const createBeneficiary =
  ({ formData, failCallback }) =>
  async (dispatch, getState) => {
    const getRequiredFields = (formData, beneficiaryTokenizationPageObject) => ({
      payout_method_type: formData?.pomt || '',
      beneficiary_country:
        beneficiaryTokenizationPageObject.beneficiary_country ||
        formData?.country ||
        undefined,
      first_name:
        formData?.requiredFields?.[BENEFICIARY_FORM_FIELDS.first_name] || undefined,
      last_name:
        formData?.requiredFields?.[BENEFICIARY_FORM_FIELDS.last_name] || undefined,
      company_name:
        formData?.requiredFields?.[BENEFICIARY_FORM_FIELDS.company_name] || undefined,
      required_fields: pickBy(
        formData.requiredFields,
        (value, key) => !SPLIT_FROM_REQUIRED_FIELDS.includes(key)
      )
    });

    try {
      const {
        beneficiaryTokenization,
        global: { isPreview, isSdk }
      } = getState();

      if (isPreview) return;

      dispatch(createBeneficiaryStart());

      const beneficiaryTokenizationPageObject =
        beneficiaryTokenization.beneficiaryTokenizationPage.data;

      const { id: token } = beneficiaryTokenizationPageObject;
      const apiData = getRequiredFields(formData, beneficiaryTokenizationPageObject);
      const response = await API.post(
        `hosted/disburse/beneficiary/${token}/create`,
        apiData
      );
      const { data } = response.data;

      dispatch(updateBeneficiaryTokenizationPageStatus(STATUSES.done));

      dispatch(
        createBeneficiarySuccess({
          ...data,
          merchant_reference_id:
            beneficiaryTokenizationPageObject.merchant_reference_id || ''
        })
      );

      trackSaveBneficiary();

      if (isSdk) return;

      // TODO: test this
      history.push(getRouteWithToken(BENEFICIARY_TOKENIZATION_THANK_YOU, token));
    } catch (error) {
      showErrorAlert({ error });

      const { status } = parseError(error);
      dispatch(createBeneficiaryFail(status));

      if (failCallback) {
        failCallback(error);
      }
    }
  };
